/**
 * DependencyGraph Component
 *
 * Interactive dependency graph visualization with:
 * - Multiple view modes (high-level, summary, detailed)
 * - Real-time filtering by node type
 * - Search functionality
 * - Drill-through on click
 * - Properties panel
 * - Multiple layout algorithms
 * - Export capabilities
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  getNodesBounds,
  getViewportForBounds,
} from '@xyflow/react'
import {
  Search, Filter, RotateCcw, ZoomIn, ZoomOut, Maximize2,
  Download, Share2, X, ChevronRight, ChevronDown,
  Loader2, AlertCircle, Eye, EyeOff, Layers, Target, Image, FileText
} from 'lucide-react'
import { toPng, toSvg } from 'html-to-image'
import { nodeTypes } from './GraphNodes'
import { parseDotFile, parseCrossReference, parseProcedureModel } from '../../utils/dotParser'
import { applyLayout } from '../../utils/graphLayouts'
import './DependencyGraph.css'

// Node type colors for legend and filtering
const NODE_TYPE_CONFIG = {
  program: { color: '#4a90d9', label: 'Programs' },
  copybook: { color: '#6b8e23', label: 'Copybooks' },
  file: { color: '#d4a574', label: 'Files/Tables' },
  entry: { color: '#e74c3c', label: 'Entry Points' },
  exit: { color: '#95a5a6', label: 'Exit Points' },
  paragraph: { color: '#3498db', label: 'Paragraphs' },
  section: { color: '#9b59b6', label: 'Sections' },
  call: { color: '#9b59b6', label: 'External Calls' },
}

// Layout options
const LAYOUT_OPTIONS = [
  { value: 'hierarchical', label: 'Hierarchical (Top-Down)' },
  { value: 'hierarchical-lr', label: 'Hierarchical (Left-Right)' },
  { value: 'force', label: 'Force-Directed' },
  { value: 'radial', label: 'Radial' },
  { value: 'grid', label: 'Grid' },
]

/**
 * Properties Panel Component
 */
function PropertiesPanel({ node, onClose, onFocusNode, onShowConnections }) {
  if (!node) {
    return (
      <div className="graph-properties-panel empty">
        <div className="empty-state">
          <Target size={32} />
          <p>Select a node to view properties</p>
        </div>
      </div>
    )
  }

  const config = NODE_TYPE_CONFIG[node.data.nodeType] || { color: '#8b949e', label: 'Node' }

  return (
    <div className="graph-properties-panel">
      <div className="properties-header">
        <h3>Properties</h3>
        <button className="close-btn" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <div className="properties-content">
        {/* Type badge */}
        <div className="property-type-badge" style={{ backgroundColor: config.color }}>
          {config.label}
        </div>

        {/* Name */}
        <div className="property-item">
          <span className="property-label">Name</span>
          <span className="property-value">{node.data.label}</span>
        </div>

        {/* Sublabel (Java name, etc.) */}
        {node.data.sublabel && (
          <div className="property-item">
            <span className="property-label">Java Name</span>
            <code className="property-code">{node.data.sublabel}</code>
          </div>
        )}

        {/* Line numbers */}
        {node.data.startLine && (
          <div className="property-item">
            <span className="property-label">Source Lines</span>
            <span className="property-value">{node.data.startLine} - {node.data.endLine}</span>
          </div>
        )}

        {/* Statement count */}
        {node.data.statementCount > 0 && (
          <div className="property-item">
            <span className="property-label">Statements</span>
            <span className="property-value">{node.data.statementCount}</span>
          </div>
        )}

        {/* Cluster/Group */}
        {node.data.clusterLabel && (
          <div className="property-item">
            <span className="property-label">Group</span>
            <span className="property-value">{node.data.clusterLabel}</span>
          </div>
        )}

        {/* Actions */}
        <div className="property-actions">
          <button className="action-btn" onClick={() => onFocusNode(node.id)}>
            <ZoomIn size={14} />
            Focus
          </button>
          <button className="action-btn" onClick={() => onShowConnections(node.id)}>
            <Share2 size={14} />
            Show Connections
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Legend Component
 */
function GraphLegend({ nodeTypes, visibleTypes, onToggleType }) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  return (
    <div className={`graph-legend ${isCollapsed ? 'collapsed' : ''}`}>
      <button className="legend-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <span>Legend</span>
      </button>

      {!isCollapsed && (
        <div className="legend-items">
          {Object.entries(NODE_TYPE_CONFIG).map(([type, config]) => {
            const count = nodeTypes[type] || 0
            if (count === 0) return null

            const isVisible = visibleTypes.has(type)

            return (
              <button
                key={type}
                className={`legend-item ${isVisible ? 'visible' : 'hidden'}`}
                onClick={() => onToggleType(type)}
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: isVisible ? config.color : '#444' }}
                />
                <span className="legend-label">{config.label}</span>
                <span className="legend-count">{count}</span>
                {isVisible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Main Dependency Graph Component
 */
function DependencyGraph({
  graphData,
  mainProgram,
  viewMode = 'high-level',
  onViewModeChange,
  loading = false,
  error = null,
}) {
  const { fitView, setCenter, getNodes, getEdges } = useReactFlow()

  // Graph state
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // UI state
  const [selectedNode, setSelectedNode] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleTypes, setVisibleTypes] = useState(new Set(Object.keys(NODE_TYPE_CONFIG)))
  const [layout, setLayout] = useState('hierarchical')
  const [isLayouting, setIsLayouting] = useState(false)
  const [showProperties, setShowProperties] = useState(true)
  const [focusedNodeId, setFocusedNodeId] = useState(null)

  // Parse and set graph data when it changes
  useEffect(() => {
    if (!graphData) return

    let parsedData = { nodes: [], edges: [] }

    // Parse based on data type
    if (typeof graphData === 'string') {
      // DOT format
      parsedData = parseDotFile(graphData)
    } else if (graphData.copybook_usage || graphData.call_graph) {
      // Cross reference JSON
      parsedData = parseCrossReference(graphData, mainProgram)
    } else if (graphData.paragraphs) {
      // Procedure model JSON
      parsedData = parseProcedureModel(graphData)
    } else if (graphData.nodes && graphData.edges) {
      // Already parsed
      parsedData = graphData
    }

    if (parsedData.nodes.length > 0) {
      runLayout(parsedData.nodes, parsedData.edges, layout)
    }
  }, [graphData, viewMode])

  // Apply layout
  const runLayout = useCallback(async (nodesToLayout, edgesToLayout, layoutType) => {
    setIsLayouting(true)
    try {
      const layoutedNodes = await applyLayout(layoutType, nodesToLayout, edgesToLayout, {
        width: 1200,
        height: 800,
      })
      setNodes(layoutedNodes)
      setEdges(edgesToLayout)

      // Fit view after layout
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 300 })
      }, 50)
    } catch (err) {
      console.error('Layout error:', err)
      // Fallback: just set nodes without layout
      setNodes(nodesToLayout)
      setEdges(edgesToLayout)
    } finally {
      setIsLayouting(false)
    }
  }, [setNodes, setEdges, fitView])

  // Re-layout when layout type changes
  useEffect(() => {
    if (nodes.length > 0) {
      runLayout(nodes, edges, layout)
    }
  }, [layout])

  // Filter nodes based on search and visibility
  const filteredNodes = useMemo(() => {
    return nodes.map(node => {
      const matchesSearch = !searchTerm ||
        node.data.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.data.sublabel?.toLowerCase().includes(searchTerm.toLowerCase())

      const isTypeVisible = visibleTypes.has(node.data.nodeType)
      const isFocused = !focusedNodeId || focusedNodeId === node.id

      return {
        ...node,
        hidden: !matchesSearch || !isTypeVisible,
        className: `${node.className || ''} ${!matchesSearch && searchTerm ? 'dimmed' : ''} ${focusedNodeId && focusedNodeId !== node.id ? 'dimmed' : ''}`,
        data: {
          ...node.data,
          highlighted: matchesSearch && searchTerm,
        },
      }
    })
  }, [nodes, searchTerm, visibleTypes, focusedNodeId])

  // Filter edges based on visible nodes
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.filter(n => !n.hidden).map(n => n.id))
    return edges.filter(edge =>
      visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    )
  }, [edges, filteredNodes])

  // Calculate node type counts
  const nodeTypeCounts = useMemo(() => {
    const counts = {}
    nodes.forEach(node => {
      const type = node.data.nodeType || 'default'
      counts[type] = (counts[type] || 0) + 1
    })
    return counts
  }, [nodes])

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node)
    setShowProperties(true)
  }, [])

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
    setFocusedNodeId(null)
  }, [])

  // Toggle node type visibility
  const toggleTypeVisibility = useCallback((type) => {
    setVisibleTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }, [])

  // Focus on a specific node
  const focusOnNode = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId)
    if (node) {
      setCenter(node.position.x + 75, node.position.y + 25, { zoom: 1.5, duration: 500 })
    }
  }, [nodes, setCenter])

  // Show connections for a node
  const showConnections = useCallback((nodeId) => {
    setFocusedNodeId(nodeId)
  }, [])

  // Reset view
  const resetView = useCallback(() => {
    setSearchTerm('')
    setVisibleTypes(new Set(Object.keys(NODE_TYPE_CONFIG)))
    setFocusedNodeId(null)
    setSelectedNode(null)
    fitView({ padding: 0.2, duration: 300 })
  }, [fitView])

  // Refresh layout
  const refreshLayout = useCallback(() => {
    if (nodes.length > 0) {
      runLayout(nodes, edges, layout)
    }
  }, [nodes, edges, layout, runLayout])

  // Helper to download image
  const downloadImage = (dataUrl, filename) => {
    const link = document.createElement('a')
    link.download = filename
    link.href = dataUrl
    link.click()
  }

  // Export to PNG
  const exportToPNG = useCallback(() => {
    const graphElement = document.querySelector('.react-flow')
    if (!graphElement) return

    toPng(graphElement, {
      backgroundColor: '#0d1117',
      width: graphElement.offsetWidth,
      height: graphElement.offsetHeight,
      style: {
        width: graphElement.offsetWidth,
        height: graphElement.offsetHeight,
      },
    })
      .then((dataUrl) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
        downloadImage(dataUrl, `dependency-graph-${timestamp}.png`)
      })
      .catch((err) => {
        console.error('Failed to export PNG:', err)
      })
  }, [])

  // Export to SVG
  const exportToSVG = useCallback(() => {
    const graphElement = document.querySelector('.react-flow')
    if (!graphElement) return

    toSvg(graphElement, {
      backgroundColor: '#0d1117',
      width: graphElement.offsetWidth,
      height: graphElement.offsetHeight,
      style: {
        width: graphElement.offsetWidth,
        height: graphElement.offsetHeight,
      },
    })
      .then((dataUrl) => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)
        downloadImage(dataUrl, `dependency-graph-${timestamp}.svg`)
      })
      .catch((err) => {
        console.error('Failed to export SVG:', err)
      })
  }, [])

  // Loading state
  if (loading) {
    return (
      <div className="graph-loading">
        <Loader2 size={32} className="spin" />
        <p>Loading graph data...</p>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="graph-error">
        <AlertCircle size={32} />
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="dependency-graph">
      {/* Toolbar */}
      <div className="graph-toolbar">
        <div className="toolbar-left">
          {/* Search */}
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Layout selector */}
          <div className="layout-selector">
            <Layers size={14} />
            <select
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
              disabled={isLayouting}
            >
              {LAYOUT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="toolbar-right">
          <button className="toolbar-btn" onClick={exportToPNG} title="Export as PNG">
            <Image size={16} />
          </button>
          <button className="toolbar-btn" onClick={exportToSVG} title="Export as SVG">
            <FileText size={16} />
          </button>
          <button className="toolbar-btn" onClick={refreshLayout} disabled={isLayouting}>
            <RotateCcw size={16} className={isLayouting ? 'spin' : ''} />
          </button>
          <button className="toolbar-btn" onClick={resetView}>
            <Maximize2 size={16} />
          </button>
          <button
            className={`toolbar-btn ${showProperties ? 'active' : ''}`}
            onClick={() => setShowProperties(!showProperties)}
          >
            Properties
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="graph-content">
        <div className="graph-canvas">
          <ReactFlow
            nodes={filteredNodes}
            edges={filteredEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
            }}
          >
            <Background color="#30363d" gap={20} />
            <Controls />

            {/* Legend */}
            <Panel position="top-left">
              <GraphLegend
                nodeTypes={nodeTypeCounts}
                visibleTypes={visibleTypes}
                onToggleType={toggleTypeVisibility}
              />
            </Panel>

            {/* Layout indicator */}
            {isLayouting && (
              <Panel position="top-center">
                <div className="layout-indicator">
                  <Loader2 size={14} className="spin" />
                  <span>Calculating layout...</span>
                </div>
              </Panel>
            )}

          </ReactFlow>
        </div>

        {/* Properties panel */}
        {showProperties && (
          <PropertiesPanel
            node={selectedNode}
            onClose={() => setShowProperties(false)}
            onFocusNode={focusOnNode}
            onShowConnections={showConnections}
          />
        )}
      </div>
    </div>
  )
}

export default DependencyGraph
